export interface AxisAlignedRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

function isFinitePositiveRect(rect: DOMRectReadOnly): boolean {
  return (
    Number.isFinite(rect.width) &&
    Number.isFinite(rect.height) &&
    rect.width > 0 &&
    rect.height > 0
  );
}

/** Tight paint box for molecule SVG (matches crop overlay measurement). */
function measureVisibleMoleculeRect(svg: SVGSVGElement): DOMRect {
  const contentGroup = svg.querySelector(':scope > g[style*="pointer-events: none"]') as SVGGElement | null;
  if (contentGroup) {
    const groupRect = contentGroup.getBoundingClientRect();
    if (isFinitePositiveRect(groupRect)) return groupRect;
  }
  const textNode = svg.querySelector(':scope > text') as SVGTextElement | null;
  if (textNode) {
    const textRect = textNode.getBoundingClientRect();
    if (isFinitePositiveRect(textRect)) return textRect;
  }
  return svg.getBoundingClientRect();
}

/**
 * Map a viewport rect into export-root layout coordinates (offsetWidth/Height space),
 * accounting for CSS scale between layout size and `getBoundingClientRect()` size.
 */
export function mapClientRectToExportSpace(
  rect: DOMRectReadOnly,
  exportRect: DOMRectReadOnly,
  layoutWidth: number,
  layoutHeight: number
): AxisAlignedRect {
  const sx = layoutWidth / Math.max(1, exportRect.width);
  const sy = layoutHeight / Math.max(1, exportRect.height);
  return {
    x: (rect.left - exportRect.left) * sx,
    y: (rect.top - exportRect.top) * sy,
    width: rect.width * sx,
    height: rect.height * sy,
  };
}

/**
 * Bounding boxes around **drawn molecule ink** (reactant / product `<svg>` content),
 * in export pixel space (same coordinates as width/height on the export root).
 */
export function measureReactionMoleculeBoundsInExportSpace(exportRoot: HTMLElement): {
  reactant: AxisAlignedRect | null;
  product: AxisAlignedRect | null;
} {
  const exportRect = exportRoot.getBoundingClientRect();
  const layoutW = exportRoot.offsetWidth || 1;
  const layoutH = exportRoot.offsetHeight || 1;

  const reactSvg = exportRoot.querySelector('#reactant-molecule svg') as SVGSVGElement | null;
  const prodSvg = exportRoot.querySelector('#product-molecule svg') as SVGSVGElement | null;

  const reactInk = reactSvg ? measureVisibleMoleculeRect(reactSvg) : null;
  const productInk = prodSvg ? measureVisibleMoleculeRect(prodSvg) : null;

  const reactRect =
    reactInk && isFinitePositiveRect(reactInk)
      ? mapClientRectToExportSpace(reactInk, exportRect, layoutW, layoutH)
      : null;
  const productRect =
    productInk && isFinitePositiveRect(productInk)
      ? mapClientRectToExportSpace(productInk, exportRect, layoutW, layoutH)
      : null;

  return { reactant: reactRect, product: productRect };
}
